import type {
  InvocationMethodDefinition,
  LLMInvocationMethod,
  LLMModelInvocationProfileInput,
  ProviderNativeDefault,
} from '../../../types/admin';

/**
 * Fallback registry metadata for the invocation tab. The backend registry
 * (`GET /v1/admin/llm-models/invocation-methods`) is the source of truth and overrides these.
 */
export const BUILT_IN_METHOD_DEFINITIONS: Record<LLMInvocationMethod, InvocationMethodDefinition> =
  {
    ai_binding: {
      method: 'ai_binding',
      transport: 'binding',
      requestEnvelope: 'native_input',
      defaultBodyBuilderKey: 'ai_binding',
      defaultParserKey: 'ai_binding',
      defaultStreamParserKey: 'ai_binding_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: true,
      supportsReasoning: true,
      allowedParams: ['gateway', 'signal'],
      blockedParams: [],
    },
    cloudflare_run: {
      method: 'cloudflare_run',
      transport: 'cloudflare_rest',
      endpoint: '/ai/run',
      requestEnvelope: 'cloudflare_run',
      defaultBodyBuilderKey: 'cloudflare_run',
      defaultParserKey: 'cloudflare_run',
      defaultStreamParserKey: 'cloudflare_run_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: true,
      supportsReasoning: true,
      allowedParams: ['model', 'input'],
      blockedParams: [],
    },
    openai_chat_completions: {
      method: 'openai_chat_completions',
      transport: 'cloudflare_rest',
      endpoint: '/ai/v1/chat/completions',
      requestEnvelope: 'openai_chat',
      defaultBodyBuilderKey: 'openai_chat_completions',
      defaultParserKey: 'openai_chat_completions',
      defaultStreamParserKey: 'openai_chat_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: true,
      supportsReasoning: true,
      allowedParams: [
        'model',
        'messages',
        'max_tokens',
        'temperature',
        'stream',
        'tools',
        'response_format',
        'reasoning_effort',
        'top_p',
      ],
      blockedParams: ['input', 'instructions'],
    },
    openai_responses: {
      method: 'openai_responses',
      transport: 'cloudflare_rest',
      endpoint: '/ai/v1/responses',
      requestEnvelope: 'openai_responses',
      defaultBodyBuilderKey: 'openai_responses',
      defaultParserKey: 'openai_responses',
      defaultStreamParserKey: 'openai_responses_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: true,
      supportsReasoning: true,
      allowedParams: [
        'model',
        'input',
        'instructions',
        'max_output_tokens',
        'temperature',
        'stream',
        'tools',
        'reasoning',
        'top_p',
      ],
      blockedParams: ['messages'],
    },
    anthropic_messages: {
      method: 'anthropic_messages',
      transport: 'cloudflare_rest',
      endpoint: '/ai/v1/messages',
      requestEnvelope: 'anthropic_messages',
      defaultBodyBuilderKey: 'anthropic_messages',
      defaultParserKey: 'anthropic_messages',
      defaultStreamParserKey: 'anthropic_messages_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: false,
      supportsReasoning: true,
      allowedParams: [
        'model',
        'system',
        'messages',
        'max_tokens',
        'temperature',
        'stream',
        'tools',
        'thinking',
        'top_p',
      ],
      blockedParams: ['input', 'instructions'],
    },
    gateway_provider_native: {
      method: 'gateway_provider_native',
      transport: 'gateway_provider_native',
      requestEnvelope: 'provider_native',
      defaultBodyBuilderKey: 'gateway_provider_native',
      defaultParserKey: 'gateway_provider_native',
      defaultStreamParserKey: 'gateway_provider_native_stream',
      supportsStream: true,
      supportsTools: true,
      supportsJsonMode: true,
      supportsReasoning: true,
      allowedParams: [],
      blockedParams: [],
    },
  };

export const METHOD_LABELS: Record<LLMInvocationMethod, { label: string; desc: string }> = {
  ai_binding: {
    label: 'Cloudflare AI Binding',
    desc: 'In-worker direct binding (env.AI.run). Lowest latency for Workers AI models.',
  },
  cloudflare_run: {
    label: 'Cloudflare REST /ai/run',
    desc: 'Universal REST fallback for Cloudflare Workers AI tasks (native task input wrapped in { model, input }).',
  },
  openai_chat_completions: {
    label: 'OpenAI Chat Completions',
    desc: 'Standardized REST /ai/v1/chat/completions. Supports tool deltas, stream, json mode.',
  },
  openai_responses: {
    label: 'OpenAI Responses API',
    desc: 'Standardized REST /ai/v1/responses. Modern endpoint using instructions and response inputs.',
  },
  anthropic_messages: {
    label: 'Anthropic Messages API',
    desc: 'Standardized REST /ai/v1/messages. System + messages format with max_tokens requirement.',
  },
  gateway_provider_native: {
    label: 'Cloudflare AI Gateway Provider Native',
    desc: 'Custom provider route via AI Gateway (https://gateway.ai.cloudflare.com/v1/{account}/{gateway}/{provider}/{endpoint}).',
  },
};

const EXECUTION_MODES = ['cheap', 'normal', 'expensive'] as const;

export const NATIVE_SCHEMA_LABELS: Record<string, string> = {
  native_messages: 'messages[] (Workers AI @cf/*, OpenAI chat models)',
  native_responses: 'input / instructions (OpenAI Responses models, gpt-5.x)',
  native_gemini: 'contents[] / generationConfig (Google Gemini)',
  native_image_bytes: 'image as byte array + prompt (Workers AI vision, e.g. llava)',
};

/**
 * Initial provider-native fields for a new profile: slug = the model's provider code, path and
 * builder/parser keys from the backend registry defaults for that slug. Unknown provider → only the
 * slug is prefilled and the admin must complete the rest (never default to another provider).
 */
export function providerNativeProfileDefaults(
  providerCode: string | undefined,
  defaults: ProviderNativeDefault[] | undefined
): Pick<
  LLMModelInvocationProfileInput,
  'providerSlug' | 'endpointPath' | 'providerModelCode' | 'bodyBuilderKey' | 'parserKey'
> {
  const slug = providerCode?.trim().toLowerCase() || null;
  const match = slug ? defaults?.find((d) => d.slug === slug) : undefined;
  return {
    providerSlug: slug,
    endpointPath: match?.endpointPath ?? null,
    providerModelCode: null,
    bodyBuilderKey: match?.bodyBuilderKey ?? null,
    parserKey: match?.parserKey ?? null,
  };
}
